// QC Inspector Qualification — create/edit form (closure).
//
// Redesigned 2026-05-05 (Phase 2.3, Class P) onto the PaperForm helper
// library. AISC 360 §N4 + AISC 207-25 §2.3.1 QCI competence record.

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link as RouterLink } from "react-router-dom";
import {
  Stack, Typography, Breadcrumbs, Box, Button, Alert, Grid,
  TextField, FormControl, InputLabel, Select, MenuItem, FormControlLabel,
  Checkbox, CircularProgress, IconButton,
} from "@mui/material";
import { Helmet } from "react-helmet-async";
import { toast } from "react-toastify";
import { ArrowBackRounded, BadgeOutlined } from "@mui/icons-material";

import { qciApi, QCI_METHOD_LABELS } from "../../utils/qcInspectorQualificationsApi";
import {
  PaperFormCard, PaperFormHeader, PaperFormSection, PaperFormFooter,
  FieldLabel,
} from "../../components/PaperForm";

const EMPTY = {
  inspector_name: "",
  qualification_class: "AISC_360_N4",
  qualification_method: "internal_training",
  documented_experience_years: "",
  experience_summary: "",
  certified_at_date: "",
  certified_to_date: "",
  is_internal: true,
  contractor_company_name: "",
  status: "active",
  status_reason: "",
  notes: "",
};

const STATUS_OPTIONS = ["active", "suspended", "expired", "revoked"];

export default function QcInspectorQualificationForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id && id !== "new";
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isEdit) return;
    qciApi.get(id).then((res) => {
      const r = res?.data?.data;
      if (r) setForm({
        inspector_name: r.inspector_name || "",
        qualification_class: r.qualification_class || "AISC_360_N4",
        qualification_method: r.qualification_method || "internal_training",
        documented_experience_years: r.documented_experience_years || "",
        experience_summary: r.experience_summary || "",
        certified_at_date: r.certified_at_date || "",
        certified_to_date: r.certified_to_date || "",
        is_internal: r.is_internal !== false,
        contractor_company_name: r.contractor_company_name || "",
        status: r.status || "active",
        status_reason: r.status_reason || "",
        notes: r.notes || "",
      });
    }).catch((e) => setError(e.message)).finally(() => setLoading(false));
  }, [id, isEdit]);

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);
    if (!form.inspector_name.trim()) { setError("Inspector Name is required."); return; }
    if (!form.is_internal && !form.contractor_company_name.trim()) {
      setError("Contractor Company Name is required when the inspector is not internal (GR §5.6 invariant).");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        documented_experience_years: form.documented_experience_years ? parseFloat(form.documented_experience_years) : null,
        certified_at_date: form.certified_at_date || null,
        certified_to_date: form.certified_to_date || null,
        contractor_company_name: form.is_internal ? null : form.contractor_company_name,
      };
      if (isEdit) await qciApi.update(id, payload);
      else await qciApi.create(payload);
      toast.success(isEdit ? "Updated" : "Created");
      navigate("/qc-inspector-qualifications");
    } catch (err) {
      const msg = err?.response?.data?.error || err.message;
      setError(msg);
      toast.error(msg);
    } finally { setSaving(false); }
  }

  const certDate = useMemo(() => {
    if (form.certified_at_date) return new Date(form.certified_at_date).toLocaleDateString();
    return new Date().toLocaleDateString();
  }, [form.certified_at_date]);

  if (loading) {
    return (
      <Box sx={{ pt: 6, textAlign: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Helmet><title>{isEdit ? "Edit" : "New"} QC Inspector | SFM</title></Helmet>

      <Box sx={{ bgcolor: "#fafafa", minHeight: "100vh" }}>
        <Box sx={{ maxWidth: "md", mx: "auto", pt: 3, px: { xs: 2, md: 3 } }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Breadcrumbs>
              <RouterLink to="/dashboard/app" style={{ color: "inherit", textDecoration: "none" }}>Home</RouterLink>
              <RouterLink to="/qc-inspector-qualifications" style={{ color: "inherit", textDecoration: "none" }}>
                QC Inspector Qualifications
              </RouterLink>
              <Typography color="text.primary">{isEdit ? "Edit" : "New"}</Typography>
            </Breadcrumbs>
            <IconButton onClick={() => navigate("/qc-inspector-qualifications")} aria-label="Back">
              <ArrowBackRounded />
            </IconButton>
          </Stack>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        </Box>

        <PaperFormCard>
          <PaperFormHeader
            formId="SFM · QC Inspector Qualification"
            title={isEdit ? "Edit QC Inspector" : "Add QC Inspector"}
            subtitle="AISC 360 §N4 · AISC 207-25 §2.3.1 — inspector competence record"
            status={form.status.toUpperCase()}
            number={form.inspector_name || (isEdit ? `#${id}` : "Assigned automatically")}
            numberLabel="Inspector"
            date={certDate}
            icon="badge"
          />

          <Box component="form" onSubmit={handleSubmit}>
            <PaperFormSection label="Identity">
              <Box>
                <FieldLabel required>Inspector Name</FieldLabel>
                <TextField
                  fullWidth size="small"
                  value={form.inspector_name}
                  onChange={(e) => setForm({ ...form, inspector_name: e.target.value })}
                  placeholder="Full legal name"
                />
              </Box>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.is_internal}
                    onChange={(e) => setForm({ ...form, is_internal: e.target.checked })}
                  />
                }
                label="Internal (A3 employee). Uncheck for contracted inspector (GR §5.6 contractor pattern)."
              />

              {!form.is_internal && (
                <Box>
                  <FieldLabel required hint="GR §5.6 contractor pattern requires this">
                    Contractor Company Name
                  </FieldLabel>
                  <TextField
                    fullWidth size="small"
                    value={form.contractor_company_name}
                    onChange={(e) => setForm({ ...form, contractor_company_name: e.target.value })}
                    placeholder="e.g., Acme Inspection Services LLC"
                  />
                </Box>
              )}
            </PaperFormSection>

            <Grid container spacing={4} sx={{ mb: 4 }}>
              <Grid item xs={12} md={6}>
                <PaperFormSection label="Qualification" bottomPadding={0}>
                  <Box>
                    <FieldLabel hint="default: AISC_360_N4">Qualification Class</FieldLabel>
                    <TextField
                      fullWidth size="small"
                      value={form.qualification_class}
                      onChange={(e) => setForm({ ...form, qualification_class: e.target.value })}
                    />
                  </Box>
                  <Box>
                    <FieldLabel>Method</FieldLabel>
                    <TextField
                      select fullWidth size="small"
                      value={form.qualification_method}
                      onChange={(e) => setForm({ ...form, qualification_method: e.target.value })}
                    >
                      {Object.entries(QCI_METHOD_LABELS).map(([v, l]) => (
                        <MenuItem key={v} value={v}>{l}</MenuItem>
                      ))}
                    </TextField>
                  </Box>
                  <Box>
                    <FieldLabel hint="years of documented experience">
                      Experience
                    </FieldLabel>
                    <TextField
                      fullWidth size="small" type="number"
                      inputProps={{ step: 0.5 }}
                      value={form.documented_experience_years}
                      onChange={(e) => setForm({ ...form, documented_experience_years: e.target.value })}
                      placeholder="e.g., 5.0"
                    />
                  </Box>
                </PaperFormSection>
              </Grid>

              <Grid item xs={12} md={6}>
                <PaperFormSection label="Validity Window" bottomPadding={0}>
                  <Box>
                    <FieldLabel>Certified At</FieldLabel>
                    <TextField
                      fullWidth size="small" type="date"
                      InputLabelProps={{ shrink: true }}
                      value={form.certified_at_date}
                      onChange={(e) => setForm({ ...form, certified_at_date: e.target.value })}
                    />
                  </Box>
                  <Box>
                    <FieldLabel hint="cert expiry — drives status auto-flip">
                      Certified To
                    </FieldLabel>
                    <TextField
                      fullWidth size="small" type="date"
                      InputLabelProps={{ shrink: true }}
                      value={form.certified_to_date}
                      onChange={(e) => setForm({ ...form, certified_to_date: e.target.value })}
                    />
                  </Box>
                  <Box>
                    <FieldLabel>Status</FieldLabel>
                    <TextField
                      select fullWidth size="small"
                      value={form.status}
                      onChange={(e) => setForm({ ...form, status: e.target.value })}
                    >
                      {STATUS_OPTIONS.map((s) => (
                        <MenuItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</MenuItem>
                      ))}
                    </TextField>
                  </Box>
                  {form.status !== "active" && (
                    <Box>
                      <FieldLabel hint="captured in audit_log">
                        Status Reason
                      </FieldLabel>
                      <TextField
                        fullWidth size="small"
                        value={form.status_reason}
                        onChange={(e) => setForm({ ...form, status_reason: e.target.value })}
                        placeholder="Why was status changed?"
                      />
                    </Box>
                  )}
                </PaperFormSection>
              </Grid>
            </Grid>

            <PaperFormSection label="Experience Summary" hint="brief narrative · evidence-of-competence">
              <TextField
                fullWidth multiline rows={3} size="small"
                value={form.experience_summary}
                onChange={(e) => setForm({ ...form, experience_summary: e.target.value })}
                placeholder="e.g., 5 yrs structural-steel inspection on AISC-certified projects in MA + NY; AWS CWI cert; trained on AISC 207-25 §2.3.1 evaluation criteria"
              />
            </PaperFormSection>

            <PaperFormSection label="Notes" hint="optional · internal context">
              <TextField
                fullWidth multiline rows={2} size="small"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
              />
            </PaperFormSection>

            <PaperFormFooter
              citation="AISC 360 §N4 · AISC 207-25 §2.3.1 — QC inspector qualification"
            />

            <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mt: 4 }}>
              <Button
                variant="text"
                onClick={() => navigate("/qc-inspector-qualifications")}
                disabled={saving}
                sx={{ color: "text.secondary" }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                startIcon={<BadgeOutlined />}
                disabled={saving}
                sx={{
                  px: 3,
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                }}
              >
                {saving ? "Saving…" : isEdit ? "Save changes" : "Create QCI Record"}
              </Button>
            </Stack>
          </Box>
        </PaperFormCard>
      </Box>
    </>
  );
}
